/**
 * RouteAccessGuard - Blocks page access when user lacks menu permission.
 * Uses Access Control API (menu_keys) to check if user can access the current route.
 * Redirects to dashboard when access is denied.
 */
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { accessControlService } from '../../services/accessControlService';
import { getRequiredMenuKeys } from '../../utils/routeAccessConfig';
import { getResolvedLandingPage } from '../../utils/userSettings';

const MENU_KEYS_CACHE_MS = 60000; // 1 min
let menuKeysCache = null;
let menuKeysCacheTime = 0;

export default function RouteAccessGuard({ children }) {
  const location = useLocation();
  const navigate = useNavigate();
  const [allowed, setAllowed] = useState(null);

  useEffect(() => {
    let cancelled = false;
    const pathname = location.pathname || '';

    const check = async () => {
      const requiredKeys = getRequiredMenuKeys(pathname);

      if (!requiredKeys || requiredKeys.length === 0) {
        setAllowed(true);
        return;
      }

      try {
        let menuKeys;
        if (menuKeysCache && Date.now() - menuKeysCacheTime < MENU_KEYS_CACHE_MS) {
          menuKeys = menuKeysCache;
        } else {
          const res = await accessControlService.getMyAccess();
          menuKeys = res?.data?.data?.menuKeys || [];
          menuKeysCache = menuKeys;
          menuKeysCacheTime = Date.now();
        }

        const hasAccess = requiredKeys.some((key) => menuKeys.includes(key));
        if (!cancelled) {
          setAllowed(hasAccess);
          if (!hasAccess) {
            const fallback = await getResolvedLandingPage();
            navigate(fallback, { replace: true });
          }
        }
      } catch (err) {
        if (!cancelled) {
          setAllowed(true);
        }
      }
    };

    check();
    return () => { cancelled = true; };
  }, [location.pathname, navigate]);

  useEffect(() => {
    const invalidate = () => {
      menuKeysCache = null;
      menuKeysCacheTime = 0;
    };
    window.addEventListener('menu-access-updated', invalidate);
    return () => window.removeEventListener('menu-access-updated', invalidate);
  }, []);

  if (allowed === null) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Checking access...</span>
        </div>
      </div>
    );
  }

  if (allowed === false) {
    return null;
  }

  return children;
}
